/** CLOSED JOINTS OF 45° WALLS — what the plan, the DWG export and 3D draw where
 *  a wall meets another at 45° or 135° (OB-42).
 *
 *  The document keeps every wall a rectangle around its axis (Tudor's ruling,
 *  04.11.2026): rooms, areas and structure read those rectangles. At a right
 *  angle a butt joint closes, since one wall runs to the other's face. At 45°
 *  it does not: the square end leaves an empty triangle on one side and, at a
 *  corner, a tip sticking out on the other — the architect's «pereții
 *  intersectați lasă un triunghi gol la intersecție». So only the DRAWN ring
 *  changes, here, and only at those joints:
 *
 *    - an L corner (both walls end near the axes' crossing): the inner faces —
 *      the sides toward the other wall's body — meet at one point, the outer
 *      faces at another, and each wall's end corners move there, as Revit's
 *      plan cleanup draws an angled corner;
 *    - a T (one wall ends on the other's side): the ending wall's two end
 *      corners move to where its faces cross the other wall's axis, inside its
 *      body, so nothing shows past either face.
 *
 *  Any other end keeps its corners; a wall that is not touched comes back as
 *  the same ring object. */
import type { Perete, Pt } from "./doc";

/** How far from 45° (or 135°) two walls' axes may cross and still be closed:
 *  the importer's own tolerance for a diagonal (`finalizare.DIAGONALA_GRADE`).
 *  Sharper or flatter joints are left butted — at 5° a mitre would reach 3 m. */
const TOLERANCE_DEG = 5;

type Wall = {
  a: Pt; b: Pt; u: Pt; n: Pt; h: number; L: number;
};

function wallOf(w: Perete): Wall | null {
  if (w.bulge || !w.ax || !(w.grosime > 0)) return null;
  const [a, b] = w.ax;
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (L < 1e-9) return null;
  const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  return { a, b, u, n: [-u[1], u[0]], h: w.grosime / 2, L };
}

/** Where the line through `p` along `d` crosses the line through `q` along
 *  `e`, or `null` when they are parallel. */
function cross(p: Pt, d: Pt, q: Pt, e: Pt): Pt | null {
  const den = d[0] * e[1] - d[1] * e[0];
  if (Math.abs(den) < 1e-12) return null;
  const s = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / den;
  return [p[0] + d[0] * s, p[1] + d[1] * s];
}

const dist = (p: Pt, q: Pt) => Math.hypot(p[0] - q[0], p[1] - q[1]);

/** The corners of a wall end: [end][side], side 0 = +n, 1 = −n. */
type Corners = [[Pt, Pt], [Pt, Pt]];

function cornersOf(z: Wall): Corners {
  const c = (p: Pt, s: number): Pt => [p[0] + z.n[0] * z.h * s, p[1] + z.n[1] * z.h * s];
  return [[c(z.a, 1), c(z.a, -1)], [c(z.b, 1), c(z.b, -1)]];
}

/** The face line of `z` on side `s` (+1 along +n, −1 along −n). */
const faceAt = (z: Wall, s: number): Pt =>
  [z.a[0] + z.n[0] * z.h * s, z.a[1] + z.n[1] * z.h * s];

/** The ring to draw for each wall, by index: closed at 45°/135° joints. */
export function closedWallOutlines(walls: readonly Perete[]): Pt[][] {
  const z = walls.map(wallOf);
  const rings: (Corners | null)[] = walls.map(() => null);
  const corners = (i: number) => (rings[i] ??= cornersOf(z[i]!));

  for (let i = 0; i < walls.length; i++) {
    const A = z[i];
    if (!A) continue;
    for (let j = i + 1; j < walls.length; j++) {
      const B = z[j];
      if (!B) continue;
      const cos = Math.min(1, Math.abs(A.u[0] * B.u[0] + A.u[1] * B.u[1]));
      if (Math.abs((Math.acos(cos) * 180) / Math.PI - 45) > TOLERANCE_DEG) continue;
      const X = cross(A.a, A.u, B.a, B.u);
      if (!X) continue;
      const lim = 2 * (A.h + B.h);
      const endA = ([0, 1] as const).find((e) => dist(e ? A.b : A.a, X) <= lim);
      const endB = ([0, 1] as const).find((e) => dist(e ? B.b : B.a, X) <= lim);
      if (endA !== undefined && endB !== undefined) {
        closeCorner(A, endA, B, endB, corners(i), corners(j));
      } else if (endA !== undefined && onAxis(B, X)) {
        closeT(A, endA, B, corners(i));
      } else if (endB !== undefined && onAxis(A, X)) {
        closeT(B, endB, A, corners(j));
      }
    }
  }
  return walls.map((w, i) => {
    const c = rings[i];
    return c ? [c[0][0], c[1][0], c[1][1], c[0][1]] : w.inel;
  });
}

/** Does the axes' crossing `X` fall within the length of `B`? */
function onAxis(B: Wall, X: Pt): boolean {
  const t = (X[0] - B.a[0]) * B.u[0] + (X[1] - B.a[1]) * B.u[1];
  return t > 0 && t < B.L;
}

/** The direction from the end `cap` of `z` into its body. */
const intoBody = (z: Wall, cap: 0 | 1): Pt => (cap ? [-z.u[0], -z.u[1]] : z.u);

function closeCorner(A: Wall, ea: 0 | 1, B: Wall, eb: 0 | 1, ca: Corners, cb: Corners) {
  // The inner side of each wall is the one the other wall's body lies on.
  const dA = intoBody(A, ea);
  const dB = intoBody(B, eb);
  const sA = Math.sign(A.n[0] * dB[0] + A.n[1] * dB[1]) || 1;
  const sB = Math.sign(B.n[0] * dA[0] + B.n[1] * dA[1]) || 1;
  const I = cross(faceAt(A, sA), A.u, faceAt(B, sB), B.u);
  const O = cross(faceAt(A, -sA), A.u, faceAt(B, -sB), B.u);
  if (!I || !O) return;
  const side = (s: number) => (s > 0 ? 0 : 1);
  ca[ea][side(sA)] = I;
  ca[ea][side(-sA)] = O;
  cb[eb][side(sB)] = I;
  cb[eb][side(-sB)] = O;
}

function closeT(A: Wall, ea: 0 | 1, B: Wall, ca: Corners) {
  for (const s of [1, -1]) {
    const q = cross(faceAt(A, s), A.u, B.a, B.u);
    if (q) ca[ea][s > 0 ? 0 : 1] = q;
  }
}
